import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAutocomplete } from 'component/Layout/Autocomplete/__hooks/useAutocomplete';
import DefaultAutocompleter from 'component/Layout/Autocomplete/__partials/Autocompleter';
import { ACCustomClass } from 'component/Layout/Autocomplete/types';
import Cta from 'component/Layout/Cta';
import { CtaType } from 'component/Layout/Cta/types';
import Loader from 'components/loader';

import classNames from 'classnames';
import { Feature } from 'config/features';

import { useFeature } from 'hooks/useFeature';
import { QaIdentifier, useQa } from 'hooks/useQa';
import { useDebounce } from 'hooks/Wait/useDebounce';

import ErrorBoundary from 'utility/Debug/ErrorBoundary';
import { lazyRetry } from 'utility/Lazy/Retry';
import { trackGa4Event } from 'utility/Track/trackingHelper';

import { Iata } from 'types/Search/airport';
import { AirportValue } from 'types/Search/filter';
import { notSet } from 'types/tracking';

import {
    useAirportAutocompleterAction,
    useAirportAutocompleterState,
} from './__context';
import {
    getAirportLabelList,
    getCountryTransKeyByCode,
} from './__helper/airport';
import { useClickCallbacks } from './__hooks/useClickCallbacks';
import styles from './__partials/Airport.module.scss';
import Chip from './__partials/List/Chip';
import InitialList from './__partials/List/InitialList';
import { AirportAcResponse, ItemType } from './types';

const LazyAutocompleterList = lazy(() =>
    lazyRetry(() => import('./__partials/List/AutocompleterResult'))
);

interface AirportRecommendation {
    airport: {
        code: string;
        name: string;
    };
}

interface SearchFormDefaults {
    airportRecommendations?: AirportRecommendation[];
}

interface BackendInformation {
    components?: {
        backendInformationFunc?: {
            getData(key: string): SearchFormDefaults;
        };
    };
}

interface SearchProps {
    airportValue: AirportValue<Iata>;
    onSubmitValue: (value: AirportValue<Iata>) => void;
    placeholder: string;
    label: string;
    customClassList?: ACCustomClass;
}

const Search = ({
    airportValue,
    onSubmitValue,
    placeholder,
    label,
    customClassList,
}: SearchProps) => {
    const airportState = useAirportAutocompleterState();
    const airportAction = useAirportAutocompleterAction();
    const qaAttr = useQa(QaIdentifier.Search_Form_Airport_Layer);
    const debouncer = useDebounce();
    const { t } = useTranslation();
    const {
        toggle,
        onToggle,
        action,
        inputValue,
        state,
        setInputValue,
        autocompleteWrapperRef,
        onSearch,
    } = useAutocomplete(airportState, airportAction);
    const isS1S4MandatoryActive = useFeature(Feature.S1S4Mandatory);
    const [hasListEntries, setHasListEntries] = useState(false);
    const [isInitialList, setIsInitialList] = useState(false);
    const [isEmptyMessage, setIsEmptyMessage] = useState(false);
    const [lastValidInput, setLastValidInput] = useState(inputValue);
    const [currentValue, setCurrentValue] = useState('');
    const handleToggle = useCallback(
        (open: boolean) => {
            if (open && !toggle) {
                // Get nearest airports from backend information
                const _ns = (window?.c24?.travelapi ??
                    window?._ns ??
                    {}) as BackendInformation;

                const ipAirports =
                    _ns.components?.backendInformationFunc?.getData(
                        'searchFormDefaults'
                    );
                const nearestAirportList =
                    ipAirports?.airportRecommendations || [];

                const nearestAirports = nearestAirportList.map(
                    (item: AirportRecommendation) => item.airport.code
                );

                trackGa4Event('autocompleterAirport_click_open', {
                    result_list: nearestAirports.length
                        ? nearestAirports.join(',')
                        : notSet,
                });
            }
            onToggle(open);
        },
        [onToggle, toggle]
    );
    const {
        onDeleteClick,
        onGroupClick,
        onClick,
        onCheckboxClick,
        onChipClick,
        onLastSearchesClick,
    } = useClickCallbacks({
        action,
        setLastValidInput,
        airportState,
        onToggle: handleToggle,
        setInputValue,
        onSubmitValue,
    });
    const onSearch_ = useCallback(
        async (value: string) => {
            setInputValue(value);
            setCurrentValue(value);
            setHasListEntries(false);
            setIsInitialList(value.length === 0);
            setIsEmptyMessage(false);
            // wait for search, if input is ready
            // (500ms after last input, execute function)
            debouncer(async () => {
                const results = await onSearch(value);
                const hideResult = !!(
                    isS1S4MandatoryActive &&
                    results?.length &&
                    results[0][0].type === ItemType.OnlyRegion
                );
                // set results
                if (results?.length && !hideResult) {
                    action.setResults(results);
                    setHasListEntries(results.length > 0);
                    // if searchinput is empty, set results empty
                } else if (results === null || hideResult) {
                    action.setResults([]);
                }
                setIsEmptyMessage(results?.length === 0 || hideResult);
            }, 500);
        },
        [action, debouncer, isS1S4MandatoryActive, onSearch, setInputValue]
    );
    const getPlaceholder = (): string => {
        if (toggle && airportState.currentValue?.size) {
            return t('TRANS-more-airports');
        }
        return (
            placeholder ??
            `${t(
                getCountryTransKeyByCode(t('TRANS-locale-country-code'))
            )} - ${t('TRANS-all-airports')}`
        );
    };
    const onAirportSelect = useCallback(
        (
            selectedAirports: AirportValue<Iata>,
            itemList?: AirportAcResponse
        ) => {
            const selectedAirport = [...selectedAirports].join(',') || notSet;
            const resultList =
                itemList?.[0]
                    ?.filter((airport) => {
                        return airport.type === 'ac-airport';
                    })
                    .map((airport) => {
                        return airport.value;
                    })
                    .join(',') ?? notSet;
            trackGa4Event('autocompleterAirport_click_add', {
                search_term: currentValue || notSet,
                result_list: resultList,
                selected: selectedAirport,
            });

            onSubmitValue(selectedAirports);
            onToggle(false);
        },
        [onSubmitValue, onToggle, currentValue]
    );

    useEffect(() => {
        if (!toggle) {
            setIsEmptyMessage(false);
            setHasListEntries(false);
            setIsInitialList(false);
            action.setValue(airportValue);
            setInputValue(getAirportLabelList(airportValue, t));
        } else {
            setInputValue('');
        }
    }, [action, airportValue, lastValidInput, setInputValue, t, toggle]);

    return (
        <DefaultAutocompleter
            autocompleterWrapperRef={
                autocompleteWrapperRef as React.RefObject<HTMLDivElement>
            }
            placeholder={getPlaceholder()}
            label={label}
            onSearchInputClick={() => {
                handleToggle(true);
                setIsInitialList(true);
            }}
            toggle={toggle}
            onSearch={onSearch_}
            searchValue={inputValue}
            customClassList={{
                Input: customClassList?.Input,
                InputWrapper: classNames(
                    styles.InputWrapper,
                    customClassList?.InputWrapper
                ),
                Label: classNames(styles.Label, customClassList?.Label),
            }}
            qaId={QaIdentifier.Search_Form_Airport_Input}
        >
            <div
                className={classNames(
                    styles.ListContainer,
                    customClassList?.Layer
                )}
                {...qaAttr}
            >
                {!!airportState.currentValue.size && (
                    <Chip
                        entryList={airportState.currentValue}
                        onChipClick={onChipClick}
                        onDeleteClick={onDeleteClick}
                    />
                )}
                {(isInitialList || hasListEntries) && (
                    <>
                        {!hasListEntries && isInitialList && (
                            <InitialList
                                onLastSearchesClick={onLastSearchesClick}
                                onClick={onCheckboxClick}
                                onCountryClick={onGroupClick}
                                onRegionClick={onGroupClick}
                            />
                        )}
                        {hasListEntries && (
                            <ErrorBoundary>
                                <Suspense fallback={<Loader />}>
                                    <LazyAutocompleterList
                                        acResponseList={state.results}
                                        onClick={onCheckboxClick}
                                        onAcRegionClick={onClick}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        )}
                        <div className={styles.Button}>
                            <Cta
                                type={CtaType.BUTTON}
                                onClick={() =>
                                    onAirportSelect(
                                        state.currentValue,
                                        state.results
                                    )
                                }
                            >
                                {t('TRANS-apply')}
                            </Cta>
                        </div>
                    </>
                )}
            </div>
            {isEmptyMessage && (
                <div
                    className={classNames(
                        styles.ListContainer,
                        customClassList?.Layer
                    )}
                >
                    <div
                        className={styles.EmptyList}
                        dangerouslySetInnerHTML={{
                            __html: t(
                                'TRANS-airport-not-found-try-again-or-choose-different-airport'
                            ),
                        }}
                    />
                </div>
            )}
        </DefaultAutocompleter>
    );
};

export default Search;
